import React, { useMemo } from 'react';
import LoadingIndicator from 'src/components/LoadingIndicator';
import NavTab from 'src/components/NavTab';
import SearchBar from 'src/components/SearchBar';
import {
  ShipmentQuotesList,
  ShipmentQuotesTable
} from 'src/components/ShipmentQuotes';
import emptyImg from 'src/images/empty.svg';
import {
  Button,
  Card,
  Col,
  Container,
  Icon,
  Nav,
  Row,
  ScreenSize,
  useApp,
  useUrlState
} from 'straightline-ui';
import { addHours } from 'straightline-utils/time';

const defaultSort = { expired_at: -1 };

export default function ShipmentQuotes() {
  const app = useApp();
  const queryState = useUrlState();
  const { getInState, assignState } = queryState;
  const query = getInState('query');
  const tab = getInState('tab', 'all');
  const $sort = getInState('query.$sort', defaultSort);

  const expiryQuery = useMemo(() => {
    // const query = { $sort, $skip };
    // const query = { $skip, $sort };
    const query = {};

    if (tab === 'expired') {
      query.expired_at = { $lt: new Date() };
      query.accepted_at = null;
    } else if (tab === 'expiring') {
      query.expired_at = {
        $lt: addHours(new Date(), 24),
        $gt: new Date()
      };
      query.accepted_at = null;
    } else if (tab === 'accepted') {
      query.accepted_at = { $ne: null };
    }

    return query;
  }, [tab]);

  const {
    result: shipmentQuotes,
    isLoading,
    isError,
    error,
    isFetching
  } = app
    .service('api/shipment-quotes')
    .useFind(
      { query: { ...query, ...expiryQuery, $sort } },
      { keepPreviousData: true }
    );

  const handleTabClick = async (tab) => {
    assignState({ tab });
  };

  if (isLoading) {
    return (
      <div className="d-flex vh-100 align-items-center">
        <LoadingIndicator delay={250} />
      </div>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row className="align-items-center justify-content-center vh-100">
          <Col lg="6" className="text-center">
            <img
              src={emptyImg}
              alt="Not Found"
              className="img-fluid animated fadeIn faster mb-5"
            />
            <h1>{error.message || 'There was an unexpected error'}</h1>
            <Button to="/dashboard" color="primary" size="lg" className="mr-3">
              <Icon type="home" className="mr-2" />
              Return to Dashboard
            </Button>
            <Button to="/support" color="secondary" size="lg">
              <Icon type="send" className="mr-2" />
              Contact Straightline
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col>
          <div className="header pt-md-2">
            <div className="header-body">
              <Row className="align-items-center">
                <Col>
                  <h6 className="header-pretitle">Overview</h6>
                  <h1 className="header-title">Quotes</h1>
                </Col>
                <Col className="col-auto">
                  <Button to="/quote" color="primary">
                    <Icon type="clipboard" className="mr-2" />
                    Request Quote
                  </Button>
                </Col>
              </Row>
              <Row
                className={`align-items-center ${
                  isFetching ? 'is-fetching' : ''
                }`}
              >
                <Col>
                  <Nav tabs className="nav-overflow header-tabs">
                    <NavTab tab="all" onClick={handleTabClick} activeTab={tab}>
                      All Quotes
                    </NavTab>
                    <NavTab
                      tab="expiring"
                      onClick={handleTabClick}
                      activeTab={tab}
                    >
                      Expiring Soon
                    </NavTab>
                    <NavTab
                      tab="expired"
                      onClick={handleTabClick}
                      activeTab={tab}
                    >
                      Expired
                    </NavTab>
                    <NavTab
                      tab="accepted"
                      onClick={handleTabClick}
                      activeTab={tab}
                    >
                      Accepted
                    </NavTab>
                  </Nav>
                </Col>
              </Row>
            </div>
          </div>
          <div className={isFetching ? 'is-fetching' : ''}>
            <ScreenSize lg xl>
              {() => (
                <QuoteTable
                  shipmentQuotes={shipmentQuotes.data}
                  queryState={queryState}
                  total={shipmentQuotes.total}
                />
              )}
            </ScreenSize>
            <ScreenSize xs sm md>
              {() => (
                <QuoteCards
                  shipmentQuotes={shipmentQuotes.data}
                  queryState={queryState}
                  total={shipmentQuotes.total}
                />
              )}
            </ScreenSize>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

const sortLabels = {
  pickup_date: 'Pickup Date',
  due_date: 'Delivery Date',
  mode: 'Mode'
};

const QuoteTable = ({ shipmentQuotes, queryState, total }) => {
  return (
    <Card>
      <SearchBar
        queryState={queryState}
        total={total}
        sortLabels={sortLabels}
      />
      <ShipmentQuotesTable data={shipmentQuotes} />
    </Card>
  );
};

const QuoteCards = ({ shipmentQuotes, queryState, total }) => {
  return (
    <React.Fragment>
      <Card className="mx-n3">
        <SearchBar
          queryState={queryState}
          total={total}
          sortLabels={sortLabels}
        />
      </Card>
      <Card className="mx-n3">
        <ShipmentQuotesList data={shipmentQuotes} />
      </Card>
    </React.Fragment>
  );
};
